import { useContext, useEffect, useState } from "react";
import { AppContext, RootWrapperContainer } from "../../App";

import { useNavigate } from "react-router-dom";

import { ColorSAP } from "../../ontego-ui";
import { Packages, Puzzle, Warehouse } from "../../ontego-ui/sap_icons";

import Button from "../../ReusableComponents/Button/Button";

import Grid from "@mui/material/Grid2";

import MobileInventorySolutionsImgNew from "../../assets/img/scenario/mobile_inventory_solutions.jpg";
import MobileProductionSolutionsImgNew from "../../assets/img/scenario/mobile_production_solutions.jpg";
import WarehouseLogisticsImgNew from "../../assets/img/scenario/warehouse_logistics_scenario.jpg";
import PlantMaintenanceImgNew from "../../assets/img/scenario/plant_maintenance.jpg";
import SpecialFunctionImgNew from "../../assets/img/scenario/special_functions.jpg";

import { PaddedContainer } from "../../App";

// Function tiles for the 4 DB-backed categories (warehouse/production/inventory/
// maintenance) are fetched from GET /functions?lang=... at mount time — see the
// useEffect below. FUNCTION_SONDERFUNKTIONEN_ERWEITERUNGEN is kept as a static
// import because "special functions" are user-defined content, not a seeded DB
// function, so they never come from the /functions list.
import { FUNCTION_SONDERFUNKTIONEN_ERWEITERUNGEN } from "../../Constants/FunctionCollection";
import FunctionService from "../FunctionDetails/FunctionService";
import i18n from "../../i18n";

import {
  FUNCTION_DETAILS_PATH,
  SPECIAL_FUNCTIONS_PATH,
} from "../../Constants/Path";
import { getFunctionIconFromId } from "../../Utils/FunctionUtils";

import { usePersistedState } from "../../Utils/Utils";

import { useTranslation } from "react-i18next";
import { F_SONDERFUNKTIONEN_ERWEITERUNGEN } from "../../Constants/FunctionIds";
import RobotArm from "../../Icons/RobotArm";
import ProcessCard from "../../ReusableComponents/ProcessCard/ProcessCard";
import SAPCategoryMenu from "../../ReusableComponents/SAPCategoryMenu/SAPCategoryMenu";
import SAPProcessContent, {
  SAPProcessContentStyles,
} from "../../ReusableComponents/SAPProcessContent/SAPProcessContent";
import FileSettings from "../../Icons/FileSettings";
import { Box } from "@mui/material";
import OntegoChecklistsHero from "./OntegoChecklistsHero";

// Categories are UI concerns only (icon, i18n label keys, hero image). The
// `typeOf` field matches the DB's function.type_of column — the useEffect
// below fetches all functions from GET /functions and buckets them by that
// key. "special" has no typeOf because Sonderfunktionen are per-client
// user-generated content, not seeded DB functions.
const CATEGORIES = [
  {
    id: "warehouse",
    typeOf: "WAREHOUSE_LOGISTICS",
    Icon: Warehouse,
    labelKey: "scenario.warehouseLogistic",
    titleKey: "scenario.warehouseLogistic",
    descKey: "scenario.warehouseLogisticExp",
    image: MobileInventorySolutionsImgNew,
  },
  {
    id: "production",
    typeOf: "PRODUCTION_AND_WAREHOUSE_ENHANCE_PACKAGE",
    Icon: RobotArm,
    labelKey: "scenario.prodWarehouseEnchancePackage",
    titleKey: "scenario.prodWarehouseEnchancePackage",
    descKey: "scenario.prodWarehouseEnchancePackageExp",
    image: MobileProductionSolutionsImgNew,
  },
  {
    id: "inventory",
    typeOf: "MOBILE_INVENTORY_SOLUTIONS",
    Icon: Packages,
    labelKey: "scenario.mobileInventorySolutions",
    titleKey: "scenario.mobileInventorySolutions",
    descKey: "scenario.mobileInventorySolutionsExp",
    image: WarehouseLogisticsImgNew,
  },
  {
    id: "maintenance",
    typeOf: "PLANT_MAINTENANCE",
    Icon: FileSettings,
    labelKey: "scenario.PlantMaintenance",
    titleKey: "scenario.PlantMaintenance",
    descKey: "scenario.PlantMaintenanceExp",
    image: PlantMaintenanceImgNew,
  },
  {
    id: "special",
    typeOf: null,
    Icon: Puzzle,
    labelKey: "scenario.specialFunctionExt",
    titleKey: "scenario.specialFunctionExt",
    descKey: "scenario.specialFunctionExtExp",
    image: SpecialFunctionImgNew,
  },
];

const Scenario = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    handleChangeInspectedFunctionId,
    collection,
    handleChangeCollection,
    handleShowErrorSnackbar,
  } = useContext(AppContext);

  const [scrollYPos, setScrollYPos] = usePersistedState("scrollYPos", 0);
  const [activeCategory, setActiveCategory] = useState("warehouse");
  // Functions grouped by type_of, populated from GET /functions?lang=...
  // Shape: { WAREHOUSE_LOGISTICS: [{systemID, name, type, typeOf}, ...], ... }
  const [functionsByTypeOf, setFunctionsByTypeOf] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setScrollYPos(window.scrollY);
    };
    window.addEventListener("scroll", onScroll);

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Fetch the function list once per language change. The server preserves
  // the scenario page's curated ordering via type_of + order_index.
  useEffect(() => {
    let isCancelled = false;
    FunctionService.listFunctions(i18n.language)
      .then((res) => {
        if (isCancelled) return;
        const grouped = {};
        (res.data || []).forEach((fn) => {
          if (!grouped[fn.typeOf]) grouped[fn.typeOf] = [];
          grouped[fn.typeOf].push(fn);
        });
        setFunctionsByTypeOf(grouped);
      })
      .catch((err) => {
        if (isCancelled) return;
        // Surface failures the same way the sibling FunctionDetailsContainer
        // does — the app-context handler routes 401s to auto-logout + toast.
        if (typeof handleShowErrorSnackbar === "function") {
          handleShowErrorSnackbar(err);
        }
      });
    return () => {
      isCancelled = true;
    };
  }, [i18n.language]);

  const handleGoToFunctionConfiguration = (val) => () => {
    handleChangeInspectedFunctionId(val.systemID);
    if (val.systemID === F_SONDERFUNKTIONEN_ERWEITERUNGEN) {
      navigate(SPECIAL_FUNCTIONS_PATH);
    } else {
      navigate(FUNCTION_DETAILS_PATH);
    }
  };

  const menuCategories = CATEGORIES.map((cat) => ({
    id: cat.id,
    Icon: cat.Icon,
    label: t(cat.labelKey),
  }));

  const activeConfig = CATEGORIES.find((cat) => cat.id === activeCategory);

  return (
    <>
      <OntegoChecklistsHero />

      <RootWrapperContainer>
        <Grid container spacing={0}>
          <PaddedContainer>
            <Grid container spacing={5}>
              <Grid item size={{ md: 4 }}>
                <SAPCategoryMenu
                  categories={menuCategories}
                  activeId={activeCategory}
                  onSelect={setActiveCategory}
                />
              </Grid>

              <Grid item size={{ md: 8 }} sx={{ paddingBottom: "104px" }}>
                {activeConfig && (
                  <SAPProcessContent
                    title={t(activeConfig.titleKey)}
                    description={t(activeConfig.descKey)}
                    image={activeConfig.image}
                  >
                    {activeConfig.id === "special" ? (
                      <Button
                        size="medium"
                        type="primary"
                        theme="dark"
                        onClick={handleGoToFunctionConfiguration(
                          FUNCTION_SONDERFUNKTIONEN_ERWEITERUNGEN,
                        )}
                      >
                        {t("scenario.functionDetail")}
                      </Button>
                    ) : (
                      (functionsByTypeOf[activeConfig.typeOf] || []).map(
                        (func, index) => (
                          <SAPProcessContentStyles.ProcessGridItem
                            key={`${activeConfig.id}-${index}`}
                          >
                            <ProcessCard
                              textFunctionDetail={t(
                                "functionDetails.functionDetail",
                              )}
                              textAddedToCollection={t(
                                "functionDetails.removeFromCollection",
                              )}
                              textAddToCollection={t(
                                "functionDetails.addToCollection",
                              )}
                              func={{
                                textName: t(`scenario.${func.name}`),
                                type: func.type,
                              }}
                              icon={getFunctionIconFromId(func.systemID)}
                              onClick={handleGoToFunctionConfiguration(func)}
                              onAddToCollectionOnClick={handleChangeCollection(
                                func,
                              )}
                              isAdded={
                                collection.filter((c) => c.name === func.name)
                                  .length === 1
                              }
                            />
                          </SAPProcessContentStyles.ProcessGridItem>
                        ),
                      )
                    )}
                  </SAPProcessContent>
                )}
              </Grid>
            </Grid>
          </PaddedContainer>
        </Grid>
      </RootWrapperContainer>
    </>
  );
};

export default Scenario;
